<template>
  <MobileAccountSettings
    v-if="isMobileSettings"
    ref="mobileSettings"
    :avatar="$store.getters['user/avatar'] || require('@/assets/img/def_user.png')"
    :subscription-key="$store.getters['user/user_sub'] || ''"
    :display-name="$store.getters['user/nick_name'] || ''"
    :email="$store.getters['user/email'] || ''"
    :membership-expiry-text="membershipExpiryText"
    :nick-name="nickName"
    :is-editing-name="isEditingName"
    :is-saving-name="isSavingNickName"
    :is-loading="isLoading"
    :devices="mobileDevices"
    @update:nick-name="updateNickName"
    @edit-name="startEditName"
    @save-name="saveNickName"
    @cancel-name="cancelEditName"
    @select-avatar="$refs.avatarUploadInput.click()"
    @change-email="handleUpdateEmail"
    @logout-device="handleLogout"
    @logout-all="handleLogoutAll"
    @delete-account="handleDeleteAccount"
  />
  <div v-else class="account-settings account-settings-desktop">
    <div class="settings-title">
      <span class="title-text">
        <el-text
          size="large"
          class="setting-nav-title"
        >
          {{ $t('views.account_center.account_settings') }}
        </el-text>
      </span>
    </div>

    <!-- 头像和昵称部分 -->
    <div class="profile-section">
      <div class="profile-container">
        <UserAvatarBadge
          :size="64"
          :src="$store.getters['user/avatar'] || require('@/assets/img/def_user.png')"
          :subscription-key="$store.getters['user/user_sub']"
        />
        <div class="user-name-input">
          <div class="username-title"> <el-text
            size="default"
          >{{ $t('views.account_center.nickname') }}</el-text></div>
          <div class="nickname-container">
            <div
              v-show="!isEditingName"
              class="nickname-display"
            >
              <span><el-text
                size="default"
              >{{ $store.getters['user/nick_name'] }}</el-text></span>
              <el-icon
                class="edit-icon"
                @click="startEditName"
              ><EditPen /></el-icon>
            </div>
            <el-input
              v-show="isEditingName"
              ref="nicknameInput"
              :model-value="nickName"
              :aria-busy="isSavingNickName"
              :data-placeholder="$t('views.account_center.nickname_placeholder')"
              size="default"
              style="width: 240px"
              maxlength="100"
              @update:model-value="updateNickName"
              @blur="saveNickName"
              @keyup.enter="saveNickName"
              @keydown.esc.stop.prevent="cancelEditName"
            />
          </div>
        </div>
      </div>
      <div class="avatar-add">
        <el-button
          type="primary"
          size="small"
          link
        >
          <label
            class="add-image"
            for="uploads"
          >{{ $t('views.account_center.add_avatar') }}</label>
        </el-button>
        <div v-if="membershipExpiryText" class="membership-expiry">{{ membershipExpiryText }}</div>
      </div>
    </div>

    <!-- 邮箱地址部分 -->
    <div class="email-section">
      <div class="email-title">
        <div class="email-title-text"><el-text>{{ $t('views.account_center.email_address') }}</el-text></div>
        <span class="email-container"><el-text size="small">{{ $store.getters['user/email'] }}</el-text></span>
      </div>
      <el-button
        @click="handleUpdateEmail"
      >{{ $t('views.account_center.change_email') }}</el-button>
    </div>

    <!-- 设备管理部分 -->
    <div class="device-section">
      <div class="device-header">
        <div class="device-left">
          <div class="decice-title-text"><el-text>{{ $t('views.account_center.device_management') }}</el-text></div>
          <div class="device-tip"><el-text size="small">{{ $t('views.account_center.device_tip') }}</el-text> </div>
        </div>
        <el-button @click="handleLogoutAll">{{ $t('views.account_center.logout_all_devices') }}</el-button>
      </div>
      <!-- 骨架加载表格 -->
      <div
        v-if="isLoading"
        class="table-skeleton"
      >
        <el-skeleton animated>
          <template #template>
            <!-- 表头骨架 -->
            <div class="skeleton-header">
              <el-skeleton-item
                v-for="n in 4"
                :key="'header' + n"
                variant="text"
                class="skeleton-header-item"
              />
            </div>
            <!-- 表格内容骨架 -->
            <div
              v-for="row in 2"
              :key="'row' + row"
              class="skeleton-row"
            >
              <el-skeleton-item
                v-for="col in 4"
                :key="'col' + col"
                variant="text"
                class="skeleton-cell"
              />
            </div>
          </template>
        </el-skeleton>
      </div>
      <!-- 实际表格 -->
      <el-table
        v-else
        :data="deviceList"
        style="width: 100%"
        max-height="190px"
      >
        <!-- 图标列固定宽度 -->
        <el-table-column width="60" align="center">
          <template #default>
            <el-icon size="16"><Monitor /></el-icon>
          </template>
        </el-table-column>

        <!-- 设备名称给较大的 min-width -->
        <el-table-column
          :label="$t('views.account_center.device_name')"
          min-width="280"
        >
          <template #default="{ row }">
            <div class="device-name-container">
              <span>{{ row.device_name }}</span>
              <span
                v-if="isCurrentDevice(row.device_id)"
                class="current-device-tag"
              >
                <el-text
                  type="primary"
                  size="small"
                >{{ $t('views.account_center.current_device') }}</el-text>
              </span>
            </div>
          </template>
        </el-table-column>

        <!-- 活跃时间 -->
        <el-table-column
          :label="$t('views.account_center.last_active_time')"
          min-width="220"
        >
          <template #default="{ row }">
            <span v-if="isCurrentDevice(row.device_id)">{{ $t('views.account_center.now') }}</span>
            <span v-else>{{ row.last_login_time }}</span>
          </template>
        </el-table-column>

        <!-- 位置 -->
        <el-table-column
          prop="ip_address"
          :label="$t('views.account_center.location')"
          min-width="200"
        />

        <!-- 操作列固定在右侧 -->
        <el-table-column align="right" width="120">
          <template #default="scope">
            <el-button
              v-if="scope.row.status===0 && !isCurrentDevice(scope.row.device_id)"
              @click="handleLogout(scope.row.device_id)"
            >{{ $t('views.account_center.logout') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 删除账号部分 -->
    <div class="delete-account">
      <el-button
        type="danger"
        link
        @click="handleDeleteAccount"
      >{{ $t('views.account_center.delete_my_account') }}</el-button>
      <div class="delete-tip"><el-text size="small">{{ $t('views.account_center.delete_account_tip') }}</el-text></div>
    </div>
  </div>

  <!-- 保留同一个文件输入，避免断点切换中断异步头像读取。 -->
  <input
    id="uploads"
    ref="avatarUploadInput"
    type="file"
    style="display: none"
    accept="image/png, image/jpeg, image/gif, image/jpg"
    @change="uploadImg($event)"
  >

  <!-- 头像剪切对话框 -->
  <component
    :is="isMobileSettings ? 'el-drawer' : 'el-dialog'"
    v-model="dialogCutImg"
    class="edit-dlg account-owned-dialog"
    :close-on-click-modal="false"
    :width="isMobileSettings ? undefined : '600px'"
    :size="isMobileSettings ? 'min(92vh, 760px)' : undefined"
    :direction="isMobileSettings ? 'btt' : undefined"
    :title="$t('views.account_center.crop_image')"
    append-to-body
    @close="dialogCutImgClose"
  >
    <div class="cut-img-box">
      <el-row>
        <el-col :span="24">
          <i class="el-icon-info" />{{ $t('views.account_center.need_crop_image') }}
        </el-col>
      </el-row>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button
          v-loading="upLoading"
          type="primary"
          @click="submitCutImg()"
        >{{ $t('views.account_center.crop') }}</el-button>
        <el-button
          v-loading="upLoading"
          @click="closeCutImg()"
        >{{ $t('views.account_center.upload_without_crop') }}</el-button>
      </span>
    </template>
  </component>

  <!-- 头像上传对话框 -->
  <component
    :is="isMobileSettings ? 'el-drawer' : 'el-dialog'"
    v-model="dialogUploadImg"
    class="account-owned-dialog account-upload-dialog"
    :close-on-click-modal="false"
    :width="isMobileSettings ? undefined : '600px'"
    :size="isMobileSettings ? 'min(92vh, 760px)' : undefined"
    :direction="isMobileSettings ? 'btt' : undefined"
    :title="$t('views.account_center.upload_image')"
    append-to-body
  >
    <div class="uploadImgBox">
      <CropperImg
        ref="uploadImgDlg"
        :image="uploadImgData"
        :select-file-name="selectFileName"
        :fixed-number="uploadImgFixedNumber"
        :fixed="fixed"
        :center-box="centerBox"
        @uploadingBase64="uploadingBase64"
        @uploadSucceed="uploadImgSucceed"
      />
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="closeDialogUploadImg()">{{ $t("common.cancel") }}</el-button>
        <el-button
          type="primary"
          @click="submitUploadImg()"
        >{{ $t("common.confirm") }}</el-button>
      </span>
    </template>
  </component>

  <!--  邮箱表单验证-->
  <component
    :is="isMobileSettings ? 'el-drawer' : 'el-dialog'"
    v-model="emailDialogVisible"
    class="account-owned-dialog account-email-dialog"
    :title="$t('views.account_center.change_email_title')"
    :width="isMobileSettings ? undefined : '520px'"
    :size="isMobileSettings ? 'min(92vh, 760px)' : undefined"
    :direction="isMobileSettings ? 'btt' : undefined"
    :close-on-click-modal="false"
  >
    <MobileAccountEmailForm
      v-if="isMobileSettings"
      ref="mobileEmailForm"
      :current-email="$store.getters['user/email'] || ''"
      :step="step"
      :old-code="emailForm.oldEmailCode"
      :new-email="emailForm.newEmail"
      :new-code="emailForm.newEmailCode"
      :old-email-code-sent="oldEmailCodeSent"
      :old-email-verified="oldEmailVerified"
      :old-email-timer="oldEmailTimer"
      :new-email-timer="newEmailTimer"
      :old-code-sending="oldEmailCodeSending"
      :new-code-sending="newEmailCodeSending"
      :step-loading="stepLoading"
      :action-text="getActionButtonText"
      @old-code-change="emailForm.oldEmailCode = $event"
      @new-email-change="emailForm.newEmail = $event"
      @new-code-change="emailForm.newEmailCode = $event"
      @send-old-code="sendOldEmailCode"
      @send-new-code="sendNewEmailCode"
      @submit="handleStepAction"
    />
    <!-- 桌面表单保留原展示；步骤、草稿、倒计时和请求均由本组件持有。 -->
    <div v-else class="dialog-content">
      <div
        v-if="!$store.getters['user/email']"
        class="step1-text"
      >
        <el-text>{{ $t('views.account_center.no_email_tip') }}</el-text>
      </div>
      <div
        v-else
        class="step1-text"
      >
        <el-text>{{ $t('views.account_center.current_email_is') }}</el-text><span class="email-text"><el-text>{{ $store.getters['user/email'] }}</el-text></span>
        <span v-if="!oldEmailCodeSent"><el-text>{{ $t('views.account_center.will_send_code') }}</el-text></span>
        <span v-else-if="oldEmailVerified"><el-text>{{ $t('views.account_center.verification_completed') }}</el-text></span>
        <span v-else />
      </div>
      <!-- 第二步：旧邮箱验证码输入 -->
      <div
        v-if="step >= 2 && !oldEmailVerified && $store.getters['user/email']"
        class="verify-sectionodl"
      >
        <el-input
          v-model="emailForm.oldEmailCode"
          data-account-field="old-code"
          :data-placeholder="$t('views.account_center.enter_verification_code')"
          class="verify-input"
        >
          <template #append>
            <el-button
              data-account-action="resend-old"
              :disabled="oldEmailTimer > 0"
              @click="sendOldEmailCode"
            >
              {{ oldEmailTimer > 0 ? $t('views.account_center.retry_later', { newEmailTimer: oldEmailTimer }) : $t('views.account_center.resend') }}
            </el-button>
          </template>
        </el-input>
      </div>

      <!-- 第三步：新邮箱输入 -->
      <div
        v-if="step >= 3"
        class="new-email-section"
      >
        <p>{{ $t('views.account_center.enter_new_email') }}</p>
        <div class="newemail-inputp-content">
          <el-input
            v-model="emailForm.newEmail"
            data-account-field="new-email"
            :data-placeholder="$t('views.account_center.enter_new_email_placeholder')"
          />
        </div>
      </div>

      <!-- 第四步：新邮箱验证码 -->
      <div
        v-if="step >= 4"
        class="verify-section"
      >
        <!-- 添加提示文本 -->
        <p class="verification-tip">{{ $t('views.account_center.verification_tip', { email: emailForm.newEmail }) }}</p>

        <!-- 验证码输入框 -->
        <el-input
          v-model="emailForm.newEmailCode"
          data-account-field="new-code"
          :data-placeholder="$t('views.account_center.enter_new_email_placeholder')"
          class="verify-input"
        >
          <template #append>
            <el-button
              data-account-action="resend-new"
              :disabled="newEmailTimer > 0"
              @click="sendNewEmailCode"
            >
              {{ newEmailTimer > 0 ? $t('views.account_center.retry_later', { newEmailTimer }) : $t('views.account_center.resend') }}
            </el-button>
          </template>
        </el-input>
      </div>
    </div>

    <template v-if="!isMobileSettings" #footer>
      <span class="dialog-footer">
        <el-button
          data-account-action="submit"
          type="primary"
          :loading="stepLoading"
          @click="handleStepAction"
        >
          {{ getActionButtonText }}
        </el-button>
      </span>
    </template>
  </component>

  <!-- 删除账号弹窗-->
  <component
    :is="isMobileSettings ? 'el-drawer' : 'el-dialog'"
    v-model="accountDialogVisible"
    class="account-owned-dialog account-delete-dialog"
    :title="$t('views.account_center.delete_account')"
    :width="isMobileSettings ? undefined : '550px'"
    :size="isMobileSettings ? 'min(92vh, 760px)' : undefined"
    :direction="isMobileSettings ? 'btt' : undefined"
    :close-on-click-modal="false"
  >
    <MobileAccountDeleteConfirm
      v-if="isMobileSettings"
      :email="$store.getters['user/email'] || ''"
      :phone="$store.getters['user/phone'] || ''"
      :confirmation="deleteEmailConfirm"
      :loading="deleteAccountLoading"
      @confirmation-change="deleteEmailConfirm = $event"
      @cancel="accountDialogVisible = false"
      @confirm="confirmDeleteAccount"
    />
    <div v-else class="dialog-content">
      <p class="warning-text"><el-icon
        color="#FF9900"
        size="large"
      ><Warning /></el-icon><el-text>{{ $t('views.account_center.delete_warning') }}</el-text></p>
      <div class="confirm-instruction-wrapper">
        <span class="confirm-instruction">
          <el-text>{{ $store.getters['user/email']
            ? $t('views.account_center.confirm_by_email')
            : $t('views.account_center.confirm_by_phone') }}</el-text>
        </span>
        <span class="email-text">
          <el-text> {{ $store.getters['user/email'] || $store.getters['user/phone'] }}</el-text>
        </span>
      </div>
      <el-input
        v-model="deleteEmailConfirm"
        data-account-field="delete-confirm"
        type="text"
        :placeholder="$store.getters['user/email'] ? $t('views.account_center.enter_email_confirm') : $t('views.account_center.enter_phone_confirm')"
        class="delete-confirm-input"
      />
    </div>
    <template v-if="!isMobileSettings" #footer>
      <span class="dialog-footer">
        <el-button data-account-action="cancel" @click="accountDialogVisible = false">{{ $t("common.cancel") }}</el-button>
        <el-button
          data-account-action="delete"
          type="danger"
          :loading="deleteAccountLoading"
          :disabled="deleteEmailConfirm !== ($store.getters['user/email'] || $store.getters['user/phone'])"
          @click="confirmDeleteAccount"
        >{{ $t('views.account_center.confirm_delete_account') }}</el-button>
      </span>
    </template>
  </component>
</template>

<script>
import {
  BindNewEmail,
  getEmailResetToken,
  getCurrSubscription,
  getSysEmailCode,
  updateBaseUserInfo,
  addNewEmail,
  deleteAccount
} from '@/api/user'
import {uploadFile} from '@/api/attachment'
import {getUserDevice, logoutAllDevice, logoutDevice} from '@/api/device'
import CropperImg from '@/components/CropperImg/index.vue'
import UserAvatarBadge from '@/components/UserAvatarBadge/index.vue'
import MobileAccountSettings from '@/views/mobile/account_center/components/MobileAccountSettings.vue'
import MobileAccountEmailForm from '@/views/mobile/account_center/components/MobileAccountEmailForm.vue'
import MobileAccountDeleteConfirm from '@/views/mobile/account_center/components/MobileAccountDeleteConfirm.vue'
import {Warning} from '@element-plus/icons-vue'

export default {
  name: 'AccountSettings',
  components: {
    Warning,
    CropperImg,
    UserAvatarBadge,
    MobileAccountSettings,
    MobileAccountEmailForm,
    MobileAccountDeleteConfirm
  },
  data() {
    const settingsMediaQuery = window.matchMedia('(max-width: 768px)')
    return {
      settingsMediaQuery,
      settingsActive: true,
      avatarReadId: 0,
      isMobileSettings: settingsMediaQuery.matches,
      isSwitchingSettingsViewport: false,
      deviceList: [],
      nickName: '',
      dialogCutImg: false,
      uploadEvent: {},
      upLoading: false,
      isEditingName: false,
      isSavingNickName: false,
      nicknameEditId: 0,
      nicknameDraftRevision: 0,
      nicknameRequestId: 0,
      dialogUploadImg: false,
      uploadImgData: '',
      uploadImgFixedNumber: [100, 100],
      selectFileName: '',
      fixed: true,
      centerBox: false,
      emailDialogVisible: false,
      accountDialogVisible: false,
      deleteEmailConfirm: '',
      deleteAccountLoading: false,
      deleteDialogId: 0,
      oldEmailCodeSending: false,
      newEmailCodeSending: false,
      step: 1,
      stepLoading: false,
      emailForm: {
        oldEmailCode: '', // 旧邮箱验证码
        newEmail: '', // 新邮箱
        newEmailCode: '', // 新邮箱验证码
        resetToken: '' // 重置邮箱的token
      },
      oldEmailVerified: false, // 旧邮箱验证码是否已验证
      oldEmailCodeSent: false, // 是否已发送旧邮箱验证码
      oldEmailTimer: 0, // 旧邮箱验证码倒计时
      oldEmailTimerId: null, // 旧邮箱计时器ID
      newEmailTimer: 0, // 新邮箱计时器ID
      newEmailTimerId: null, // 新邮箱计时器ID
      isLoading: true, // 骨架加载
      membershipExpiryDate: ''
    }
  },
  computed: {
    mobileDevices() {
      return this.deviceList.map(device => {
        const isCurrent = this.isCurrentDevice(device.device_id)
        return { ...device, isCurrent, canLogout: device.status === 0 && !isCurrent }
      })
    },
    getActionButtonText() {
      if (!this.$store.getters['user/email']) {
        const textMap = {
          3: this.$t('views.account_center.send_code'),
          4: this.$t('views.account_center.bind_email')
        }
        return textMap[this.step]
      } else {
        const textMap = {
          1: this.$t('views.account_center.send_code'),
          2: this.$t('views.account_center.verify'),
          3: this.$t('views.account_center.send_code'),
          4: this.$t('views.account_center.change_email_action')
        }
        return textMap[this.step]
      }
    },
    membershipExpiryText() {
      if (!this.membershipExpiryDate) return ''
      return this.$t('views.account_center.membership_expires_on', {
        expiryDate: this.formatMembershipExpiry(this.membershipExpiryDate)
      })
    }
  },
  mounted() {
    this.settingsMediaQuery.addEventListener('change', this.handleSettingsViewportChange)
    this.getUserInfoData()
    // 初始化昵称
    this.nickName = this.$store.getters['user/nick_name']
    this.getMembershipExpiry()
    this.getDeviceList()
  },
  beforeUnmount() {
    this.settingsActive = false
    this.nicknameEditId += 1
    this.settingsMediaQuery.removeEventListener('change', this.handleSettingsViewportChange)
    if (this.oldEmailTimerId) {
      clearInterval(this.oldEmailTimerId)
      this.oldEmailTimerId = null
    }
    if (this.newEmailTimerId) {
      clearInterval(this.newEmailTimerId)
      this.newEmailTimerId = null
    }
  },
  methods: {
    getNicknameElement() {
      return this.isMobileSettings
        ? this.$refs.mobileSettings?.getNicknameElement()
        : this.$refs.nicknameInput?.input
    },
    focusNicknameInput(selection) {
      const input = this.getNicknameElement()
      if (!input) return
      input.focus({ preventScroll: true })
      if (selection) input.setSelectionRange(selection.start, selection.end, selection.direction)
    },
    handleSettingsViewportChange(event) {
      if (event.matches === this.isMobileSettings) return
      const input = this.getNicknameElement()
      const selection = this.isEditingName && input && document.activeElement === input
        ? { start: input.selectionStart, end: input.selectionEnd, direction: input.selectionDirection }
        : null
      // Element Plus 在输入法组合结束前不会更新 model，切换前保留输入框中的实际文本。
      if (selection) this.updateNickName(input.value)
      const activeAction = document.activeElement?.closest?.('[data-account-action]')
      const activeField = activeAction ? null : document.activeElement?.closest?.('[data-account-field]')
      const focusedAccountControl = activeField && input !== document.activeElement
        ? {
            type: 'field',
            name: activeField.getAttribute('data-account-field'),
            value: activeField.querySelector('input, textarea')?.value,
            start: activeField.querySelector('input, textarea')?.selectionStart,
            end: activeField.querySelector('input, textarea')?.selectionEnd,
            direction: activeField.querySelector('input, textarea')?.selectionDirection
          }
        : activeAction ? { type: 'action', name: activeAction.getAttribute('data-account-action') } : null
      if (focusedAccountControl?.type === 'field') this.syncAccountDialogField(focusedAccountControl.name, focusedAccountControl.value)
      // 仅替换展示；卸载输入产生的 blur 不应提交尚未保存的昵称。
      this.isSwitchingSettingsViewport = true
      this.isMobileSettings = event.matches
      this.$nextTick(() => {
        if (!this.settingsActive || this.isMobileSettings !== event.matches) return
        if (selection && this.isEditingName) this.focusNicknameInput(selection)
        if (focusedAccountControl) this.focusAccountDialogControl(focusedAccountControl)
        this.isSwitchingSettingsViewport = false
      })
    },
    syncAccountDialogField(name, value) {
      if (typeof value !== 'string') return
      if (name === 'old-code') this.emailForm.oldEmailCode = value
      if (name === 'new-email') this.emailForm.newEmail = value
      if (name === 'new-code') this.emailForm.newEmailCode = value
      if (name === 'delete-confirm') this.deleteEmailConfirm = value
    },
    focusAccountDialogControl(control) {
      const dialog = control.name === 'delete-confirm' || control.name === 'cancel' || control.name === 'delete'
        ? '.account-delete-dialog'
        : '.account-email-dialog'
      if (dialog === '.account-delete-dialog' ? !this.accountDialogVisible : !this.emailDialogVisible) return
      const target = control.type === 'field'
        ? document.querySelector(`${dialog} [data-account-field="${control.name}"] input, ${dialog} [data-account-field="${control.name}"] textarea`)
        : document.querySelector(`${dialog} [data-account-action="${control.name}"]`)
      if (!target) {
        document.querySelector(`${dialog} .el-dialog__headerbtn`)?.focus({ preventScroll: true })
        return
      }
      target.focus({ preventScroll: true })
      if (document.activeElement !== target) {
        document.querySelector(`${dialog} .el-dialog__headerbtn`)?.focus({ preventScroll: true })
      }
      if (control.type === 'field' && control.start !== null && control.start !== undefined && typeof target.setSelectionRange === 'function') {
        target.setSelectionRange(control.start, control.end, control.direction)
      }
    },
    getMembershipExpiry() {
      getCurrSubscription()
        .then(response => {
          const subscription = response?.data
          this.membershipExpiryDate = subscription && !Array.isArray(subscription)
            ? (subscription.expires_at || subscription.ends_at || subscription.next_billing_date || '')
            : ''
        })
        .catch(error => {
          console.error('Failed to load membership expiry', error)
          this.membershipExpiryDate = ''
        })
    },
    formatMembershipExpiry(value) {
      if (!value) return ''
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (match) return `${match[1]}.${match[2]}.${match[3]}`
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${date.getFullYear()}.${month}.${day}`
    },
    isCurrentDevice(deviceId) {
      // 添加空值判断
      const currentDeviceId = localStorage.getItem('X-Device-ID') || ''
      return deviceId === currentDeviceId
    },
    startEditName() {
      if (!this.settingsActive) return
      if (!this.isEditingName) {
        this.nicknameEditId += 1
        this.isEditingName = true
      }
      this.$nextTick(() => {
        if (this.settingsActive && this.isEditingName) this.focusNicknameInput()
      })
    },
    updateNickName(value) {
      if (!this.settingsActive || this.nickName === value) return
      this.nickName = value
      this.nicknameDraftRevision += 1
    },
    cancelEditName() {
      if (!this.settingsActive) return
      // Escape abandons this editor session; an in-flight request cannot restore it.
      this.nicknameEditId += 1
      this.updateNickName(this.$store.getters['user/nick_name'])
      this.isEditingName = false
    },
    isCurrentNicknameSave(save) {
      return this.settingsActive && this.nicknameEditId === save.editId && this.nicknameRequestId === save.requestId
    },
    async saveNickName() {
      if (!this.settingsActive || !this.isEditingName) return
      if (this.isSwitchingSettingsViewport || this.settingsMediaQuery.matches !== this.isMobileSettings) return
      // 防止重复触发
      if (this.isSavingNickName) {
        return
      }
      
      if (!this.nickName || !this.nickName.trim()) {
        this.$message.warning(this.$t('views.account_center.nickname_empty'))
        this.nickName = this.$store.getters['user/nick_name']
        this.isEditingName = false
        return
      }

      if (this.nickName === this.$store.getters['user/nick_name']) {
        this.isEditingName = false
        return
      }

      const save = {
        requestId: ++this.nicknameRequestId,
        editId: this.nicknameEditId,
        revision: this.nicknameDraftRevision,
        value: this.nickName
      }
      this.isSavingNickName = true
      try {
        const saved = await this.updateBaseUserInfoData({ nick_name: save.value }, save)
        // Keep any text entered while saving, including an edit that returns to the same value.
        if (saved && this.isCurrentNicknameSave(save) && this.nicknameDraftRevision === save.revision && this.nickName === save.value) {
          this.isEditingName = false
        }
        return saved
      } finally {
        if (this.nicknameRequestId === save.requestId) this.isSavingNickName = false
      }
    },
    // 上传头像相关方法
    uploadImg(e) {
      if (!this.settingsActive || this.upLoading) return
      this.uploadEvent = e
      if (e.target.files.length === 0) {
        return false
      }
      for (let i = 0; i < e.target.files.length; i++) {
        if (!/\.(gif|jpg|jpeg|png|bmp|GIF|JPG|PNG)$/.test(e.target.files[i].name)) {
          this.$message({
            message: this.$t('views.account_center.image_type_error'),
            type: 'error'
          })
          return false
        }
        if (e.target.files[i].size > 20 * 1024 * 1024) {
          this.$message({
            message: this.$t('common.single_image_size_no_more_than_50M'),
            type: 'error'
          })
          return false
        }
      }
      if (e.target.files.length === 1) {
        this.submitCutImg()
      } else {
        const files = e.target.files
        this.upLoading = true
        let uploadCount = 0
        for (let i = 0; i < files.length; i++) {
          const formData = new FormData()
          formData.append('file', files[i])
          uploadFile(formData).then(() => {
            uploadCount = uploadCount + 1
            if (uploadCount === files.length) {
              this.upLoading = false
              this.clearAvatarInput()
            }
          }).catch(() => {
            this.upLoading = false
            this.clearAvatarInput()
          })
        }
      }
    },

    submitCutImg() {
      const input = this.uploadEvent.target
      const file = input?.files?.[0]
      if (!file || !this.settingsActive) return
      const readId = ++this.avatarReadId
      const reader = new FileReader()
      reader.onload = e => {
        if (!this.settingsActive || readId !== this.avatarReadId) return
        let data
        if (typeof e.target.result === 'object') {
          // 把Array Buffer转化为blob 如果是base64不需要
          data = window.URL.createObjectURL(new Blob([e.target.result]))
        } else {
          data = e.target.result
        }
        this.uploadImgData = data
        this.selectFileName = file.name.replace(/\.[^.]+$/, '')
        input.value = ''
        this.dialogUploadImg = true
        this.dialogCutImgClose()
      }
      reader.readAsArrayBuffer(file)
    },

    dialogCutImgClose() {
      this.clearAvatarInput()
      this.dialogCutImg = false
    },

    clearAvatarInput() {
      if (this.$refs.avatarUploadInput) this.$refs.avatarUploadInput.value = ''
    },

    async closeCutImg() {
      const file = this.uploadEvent.target?.files?.[0]
      if (!this.settingsActive || this.upLoading || !file) return
      const formData = new FormData()
      formData.append('file', file)
      this.upLoading = true
      try {
        const response = await uploadFile(formData)
        this.clearAvatarInput()
        this.uploadEvent = {}
        if (response.data) {
          this.uploadImgSucceed(response.data)
        } else {
          this.$message({
            message: response,
            type: 'error'
          })
        }
      } catch (error) {
        console.error('Avatar upload failed:', error)
      } finally {
        this.upLoading = false
        if (this.settingsActive) this.dialogCutImgClose()
      }
    },

    uploadImgSucceed(data) {
      if (data) {
        // 更新用户头像
        const avatar = data.file
        this.updateBaseUserInfoData({ avatar })
        // 强制更新界面
        this.$store.commit('user/setAvatar', avatar)
        if (this.settingsActive) this.$forceUpdate()
      }
    },

    uploadingBase64(date) {
      this.upLoading = date
    },

    closeDialogUploadImg() {
      this.dialogUploadImg = false
    },

    submitUploadImg() {
      if (this.upLoading || !this.settingsActive) return
      this.upLoading = true
      try {
        this.$refs['uploadImgDlg'].finish()
        this.closeDialogUploadImg()
      } catch (error) {
        this.upLoading = false
        console.error('Avatar crop submission failed:', error)
      }
    },
    isCurrentEmailForm(form) {
      return this.settingsActive && this.emailDialogVisible && this.emailForm === form
    },
    async handleStepAction() {
      if (!this.isCurrentEmailForm(this.emailForm) || this.stepLoading || this.oldEmailCodeSending || this.newEmailCodeSending) return
      const form = this.emailForm
      this.stepLoading = true
      try {
        switch (this.step) {
          case 1: {
            const sent = await this.sendOldEmailCode()
            if (sent && this.isCurrentEmailForm(form)) {
              this.step = 2
            }
            break
          }
          case 2: {
            const verified = await this.verifyOldEmailCode()
            if (verified && this.isCurrentEmailForm(form)) {
              this.step = 3
            }
            break
          }
          case 3: {
            if (this.validateNewEmail()) {
              const sent = await this.sendNewEmailCode()
              if (sent && this.isCurrentEmailForm(form)) {
                this.step = 4
              }
            }
            break
          }
          case 4: {
            await this.submitEmailChange()
            break
          }
        }
      } finally {
        this.stepLoading = false
      }
    },
    async sendOldEmailCode() {
      if (!this.isCurrentEmailForm(this.emailForm) || this.oldEmailTimer > 0 || this.oldEmailCodeSending) return false

      const form = this.emailForm
      this.oldEmailCodeSending = true
      try {
        // 发送验证码，type=2表示更换邮箱
        await getSysEmailCode({
          email: this.$store.getters['user/email'],
          type: 2
        })
        // 关闭、重开或离页后的响应不能改写新表单，也不能再创建计时器。
        if (!this.isCurrentEmailForm(form)) return false

        // 清空输入框内容
        this.emailForm.oldEmailCode = ''
        this.oldEmailCodeSent = true

        // 设置倒计时
        this.oldEmailTimer = 60
        this.startOldEmailTimer()

        this.$message.success(this.$t('views.account_center.code_sent'))
        return true
      } catch (error) {
        return false
      } finally {
        this.oldEmailCodeSending = false
      }
    },
    startOldEmailTimer() {
      if (this.oldEmailTimerId) {
        clearInterval(this.oldEmailTimerId)
      }

      this.oldEmailTimerId = setInterval(() => {
        if (this.oldEmailTimer > 0) {
          this.oldEmailTimer--
        } else {
          clearInterval(this.oldEmailTimerId)
          this.oldEmailTimerId = null
        }
      }, 1000)
    },
    async verifyOldEmailCode() {
      const form = this.emailForm
      if (!this.isCurrentEmailForm(form)) return false
      const code = form.oldEmailCode
      if (!this.emailForm.oldEmailCode) {
        this.$message.warning(this.$t('views.account_center.code_required'))
        return false
      }

      try {
        // 调用获取reset_token的接口
        const response = await getEmailResetToken({
          valid_code: code
        })
        if (!this.isCurrentEmailForm(form) || form.oldEmailCode !== code) return false

        if (response.code === 0 && response.data) {
          // 保存reset_token，用于后续操作
          this.emailForm.resetToken = response.data.reset_token
          this.$message.success(this.$t('views.account_center.verification_completed'))
          this.oldEmailVerified = true
          return true
        } else {
          return false
        }
      } catch (error) {
        return false
      }
    },
    async sendNewEmailCode() {
      if (!this.isCurrentEmailForm(this.emailForm) || this.newEmailTimer > 0 || this.newEmailCodeSending) return false

      if (!this.validateNewEmail()) {
        return false
      }

      const form = this.emailForm
      const email = form.newEmail
      this.newEmailCodeSending = true
      try {
        await getSysEmailCode({
          email,
          type: 2
        })
        if (!this.isCurrentEmailForm(form) || form.newEmail !== email) return false
        // 清空输入框内容
        this.emailForm.newEmailCode = ''

        // 设置倒计时
        this.newEmailTimer = 60
        this.startNewEmailTimer()

        this.$message.success(this.$t('views.account_center.code_sent'))
        return true
      } catch (error) {
        return false
      } finally {
        this.newEmailCodeSending = false
      }
    },
    validateNewEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!this.emailForm.newEmail) {
        this.$message.warning(this.$t('views.account_center.email_required'))
        return false
      }
      if (!emailRegex.test(this.emailForm.newEmail)) {
        this.$message.warning(this.$t('views.account_center.email_invalid'))
        return false
      }
      if (this.emailForm.newEmail === this.$store.getters['user/email']) {
        this.$message.warning(this.$t('views.account_center.email_duplicate'))
        return false
      }
      return true
    },
    startNewEmailTimer() {
      if (this.newEmailTimerId) {
        clearInterval(this.newEmailTimerId)
      }

      this.newEmailTimerId = setInterval(() => {
        if (this.newEmailTimer > 0) {
          this.newEmailTimer--
        } else {
          clearInterval(this.newEmailTimerId)
          this.newEmailTimerId = null
        }
      }, 1000)
    },
    resetEmailForm() {
      this.step = 1
      this.emailForm = {
        oldEmailCode: '',
        newEmail: '',
        newEmailCode: '',
        resetToken: ''
      }
      this.oldEmailCodeSent = false
      this.oldEmailVerified = false

      if (this.oldEmailTimerId) {
        clearInterval(this.oldEmailTimerId)
        this.oldEmailTimerId = null
      }
      if (this.newEmailTimerId) {
        clearInterval(this.newEmailTimerId)
        this.newEmailTimerId = null
      }

      this.oldEmailTimer = 0
      this.newEmailTimer = 0
    },
    async getUserInfoData() {
      // 获取用户信息数据
      // const userInfo = await this.$store.dispatch('user/getUserInfo')
      this.nickName = this.$store.getters['user/nick_name']
    },
    async updateBaseUserInfoData(data, nicknameSave = null) {
      const params = {}
      if (data.avatar) {
        params.avatar = data.avatar
      }
      if (data.nick_name) {
        params.nick_name = data.nick_name
      }

      if (params.avatar || params.nick_name) {
        try {
          await updateBaseUserInfo(params)
          if (nicknameSave && !this.isCurrentNicknameSave(nicknameSave)) return false
          this.$message.success(this.$t('views.account_center.save_success'))

          // 更新本地状态
          if (data.nick_name) {
            this.$store.commit('user/setNickName', data.nick_name)
          }
          if (data.avatar) {
            this.$store.commit('user/setAvatar', data.avatar)
          }

          // 获取更新后的用户信息
          if (this.settingsActive && !this.isEditingName) this.getUserInfoData()
          return true
        } catch (e) {
          if (data.avatar) {
            this.upLoading = false
            this.clearAvatarInput()
          }
          // Nickname failures retain the shared draft for retry; avatar cleanup stays independent.
          return false
        }
      }
      return false
    },
    async submitEmailChange() {
      const form = this.emailForm
      if (!this.isCurrentEmailForm(form)) return false
      if (!this.emailForm.newEmailCode) {
        this.$message.warning(this.$t('views.account_center.code_required'))
        return false
      }

      try {
        // 检查是否是首次绑定邮箱
        const isFirstBind = !this.$store.getters['user/email']
        let response

        if (isFirstBind) {
          // 首次绑定邮箱，使用 addNewEmail 接口
          response = await addNewEmail({
            email: this.emailForm.newEmail,
            valid_code: this.emailForm.newEmailCode
          })
        } else {
          // 更改现有邮箱，使用 BindNewEmail 接口，需要 reset_token
          const resetToken = this.emailForm.resetToken
          if (!resetToken) {
            this.$message.error(this.$t('views.account_center.token_invalid'))
            this.resetEmailForm()
            this.emailDialogVisible = false
            return false
          }

          response = await BindNewEmail({
            reset_token: resetToken,
            email: this.emailForm.newEmail,
            valid_code: this.emailForm.newEmailCode
          })
        }

        if (response.code === 0) {
          // 更新成功
          this.$message.success(isFirstBind ? this.$t('views.account_center.email_bind_success') : this.$t('views.account_center.email_update_success'))
          if (this.settingsActive && this.emailForm === form) {
            this.emailDialogVisible = false
            this.resetEmailForm()
          }
          // 邮箱已在服务端变更，关闭表单或离页不取消原有的重新登录要求。
          await this.$store.dispatch('user/logout')
          await this.$router.push({ path: '/login' })
          return true
        } else {
          return false
        }
      } catch (error) {
        return false
      }
    },
    handleUpdateEmail() {
      this.resetEmailForm()
      this.emailDialogVisible = true
      if (!this.$store.getters['user/email']) {
        this.step = 3
      } else {
        this.step = 1
      }
    },
    async handleLogoutAll() {
      try {
        const params = {
          except_current: 1
        }
        const logoutall = await logoutAllDevice(params)
        if (logoutall) {
          this.$message.success(this.$t('views.account_center.logout_success'))
          // 刷新设备列表
          this.getDeviceList()
        } else {
          this.$message.error(logoutall.message || this.$t('views.account_center.operation_failed'))
        }
      } catch (error) {
        if (error !== 'cancel') { // 忽略取消操作
          console.error('登出设备失败:', error)
          this.$message.error(error.message || this.$t('views.account_center.operation_failed'))
        }
      }
    },
    async handleLogout(deviceId) {
      try {
        const currentDeviceId = localStorage.getItem('X-Device-ID')
        // 判断是否为当前设备
        if (deviceId === currentDeviceId) {
          // 如果是当前设备，先显示提示
          const loadingMessage = this.$message.info(this.$t('views.account_center.logout_current_device'))
          // 执行登出操作
          await this.$store.dispatch('user/logout')
          loadingMessage.close()
          this.$message.success(this.$t('views.account_center.logout_device_success'))
          this.$router.push('/login')
        } else {
          // 登出其他设备
          const params = { device_id: deviceId }
          const response = await logoutDevice(params)

          if (response && response.code === 0) {
            this.$message.success(this.$t('views.account_center.logout_device_success'))
            // 刷新设备列表
            this.getDeviceList()
          } else {
            this.$message.error(response.message || this.$t('views.account_center.operation_failed'))
          }
        }
      } catch (error) {
        console.error('登出设备失败:', error)
        this.$message.error(error.message || this.$t('views.account_center.logout_failed'))
      }
    },
    handleDeleteAccount() {
      this.deleteDialogId += 1
      this.deleteEmailConfirm = ''
      this.accountDialogVisible = true
    },

    async confirmDeleteAccount() {
      if (!this.settingsActive || this.deleteAccountLoading) return
      // 判断用户有邮箱还是手机号
      const userEmail = this.$store.getters['user/email']
      const userPhone = this.$store.getters['user/phone']

      // 确认验证信息是否匹配
      let isValid = false

      if (userEmail) {
        // 如果有邮箱，验证邮箱是否匹配
        isValid = this.deleteEmailConfirm === userEmail
        if (!isValid) {
          this.$message.error(this.$t('views.account_center.email_incorrect'))
          return
        }
      } else if (userPhone) {
        // 如果没有邮箱但有手机号，验证手机号是否匹配
        isValid = this.deleteEmailConfirm === userPhone
        if (!isValid) {
          this.$message.error(this.$t('views.account_center.phone_incorrect'))
          return
        }
      } else {
        this.$message.error(this.$t('views.account_center.identity_verification'))
        return
      }

      this.deleteAccountLoading = true
      const dialogId = this.deleteDialogId

      // 构建删除账号参数
      const params = {}
      if (userEmail) {
        params.email = this.deleteEmailConfirm
      } else if (userPhone) {
        params.phone = this.deleteEmailConfirm
      }

      try {
        const response = await deleteAccount(params)
        if (response && response.code === 0) {
          this.$message.success(this.$t('views.account_center.account_deleted'))
          // 跳转到登录页面
          window.location.reload()
        } else {
          this.$message.error(response?.message || this.$t('views.account_center.delete_account_failed'))
        }
      } catch (error) {
        this.$message.error(error?.message || this.$t('views.account_center.delete_account_failed'))
      } finally {
        this.deleteAccountLoading = false
        if (dialogId === this.deleteDialogId) this.accountDialogVisible = false
      }
    },
    getDeviceList() {
      this.isLoading = true
      const params = {
        page: this.currentPage,
        page_size: this.pageSize
      }
      getUserDevice(params)
        .then(response => {
          if (response) {
            // 获取当前设备ID
            const currentDeviceId = localStorage.getItem('X-Device-ID')
            // 对设备列表进行排序，将当前设备置顶
            this.deviceList = response.data.data.sort((a, b) => {
              if (a.device_id === currentDeviceId) return -1
              if (b.device_id === currentDeviceId) return 1
              return 0
            })
            this.total = response.data.total
          }
        })
        .catch(error => {
          this.$message.error(error.message || this.$t('views.account_center.get_device_list_failed'))
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>
<style lang="scss">
@import '@/assets/css/variables';
@import '@/views/mobile/account_center/styles/account_dialogs';
</style>

<style scoped lang="scss" >
@import '@/assets/css/views/account_center/components/account_settings';
</style>

